<script setup lang="ts">
import { useProfilesStore } from '@/store/modules/userProfile'
import { Button } from '@/components/ui/button'
import { Eye } from 'lucide-vue-next'
import { useToast } from 'primevue/usetoast'
import TemplateThumbnail from '@/components/share/TemplateThumbnail.vue'

const profilesStore = useProfilesStore()
const toast = useToast()

function parseItem(item: string) {
  const [idPart, variantName] = item.split('|')
  const parts = idPart.split(':')
  // Force brandId to 'betpic' as it should no longer be brand-specific in this view
  const brandId = 'betpic'
  return { bundleId: parts[0], variantId: parts[1], brandId, variantName }
}

function unhideTemplate(item: string) {
  if (!profilesStore.profile.hiddenTemplates) return
  
  profilesStore.profile.hiddenTemplates = profilesStore.profile.hiddenTemplates.filter(h => h !== item)
  
  if (profilesStore.profile.userId) {
    profilesStore.saveToDB(profilesStore.profile.userId)
  } else {
    profilesStore.saveLocal()
  }

  const { variantName } = parseItem(item)
  toast.add({ 
    severity: 'success', 
    summary: 'Template Restored', 
    detail: `${variantName} is now visible again.`, 
    life: 3000 
  })
}

function clearAll() {
  profilesStore.profile.hiddenTemplates = []
  if (profilesStore.profile.userId) {
    profilesStore.saveToDB(profilesStore.profile.userId)
  } else {
    profilesStore.saveLocal()
  }
  toast.add({ severity: 'success', summary: 'All Restored', detail: 'All templates are now visible.', life: 3000 })
}

defineExpose({ clearAll })
</script>

<template>
  <div class="flex flex-col gap-6">
    <section class="flex flex-col">
      <div class="p-1">
        <div v-if="profilesStore.profile.hiddenTemplates?.length" class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          <div 
            v-for="item in profilesStore.profile.hiddenTemplates" 
            :key="item"
            class="group relative flex flex-col bg-white dark:bg-gray-900 rounded-xl border border-border dark:border-gray-800 overflow-hidden shadow-sm hover:shadow-md transition-all"
          >
            <!-- Thumbnail Container -->
            <div class="aspect-[393/660] relative overflow-hidden bg-gray-100 dark:bg-gray-800">
              <TemplateThumbnail 
                :bundle-config="parseItem(item)" 
                :brand-id="parseItem(item).brandId"
                class="w-full h-full"
              />
              
              <!-- Hover Overlay -->
              <div class="absolute inset-0 bg-black/10 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />

              <!-- Unhide Button Overlay -->
              <Button 
                variant="secondary" 
                size="sm" 
                @click="unhideTemplate(item)"
                class="absolute top-1 right-1 sm:top-2 sm:right-2 h-7 w-7 sm:h-8 sm:w-8 p-0 max-md:px-0 rounded-full shadow-lg transition-all bg-white/90 dark:bg-gray-800/90 text-gray-900 dark:text-white hover:bg-white dark:hover:bg-gray-700"
                title="Unhide template"
              >
                <Eye class="w-4 h-4" />
              </Button>
            </div>

            <!-- Info Area -->
            <div class="p-3">
              <div class="text-xs font-bold text-gray-900 dark:text-white truncate">
                {{ parseItem(item).variantName }}
              </div>
              <div class="text-[9px] text-gray-400 font-mono mt-1">
                <span>{{ parseItem(item).bundleId.split('-')[0] }}</span>
              </div>
            </div>
          </div>
        </div>
        
        <div v-else class="py-20 flex flex-col items-center justify-center text-gray-400 text-center px-6">
          <div class="p-4 rounded-full bg-gray-50 dark:bg-gray-800 mb-4">
            <Eye class="w-8 h-8 opacity-20" />
          </div>
          <p class="text-sm font-medium">No hidden templates</p>
          <p class="text-xs mt-1">Hiding templates helps you focus on what you use most</p>
        </div>
      </div>
    </section>
  </div>
</template>
